import _isNaN from 'lodash/isNaN';
import _throttle from 'lodash/throttle';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  requestVideoPlayerCurrentTime,
  requestVideoPlayerPlay,
  updateVideoPlayerHideReaction,
  updateVideoPlayerLocalReactions,
  updateVideoPlayerSeeking,
} from 'actions/videoPlayer';
import { hasScope } from 'selectors/scopes';
import { selectIsActive } from 'selectors/viewerInsights';
import { getNewTimeSkipRanges } from 'utilities/trimming';

import AddTrimRangeTooltip from './add-trim-range-tooltip';
import EmojiTimeline from './emoji-timeline';
import LoadProgressBar from './load-progress-bar';
import PlayProgressBar from './play-progress-bar';
import PlayProgressScrubber from './play-progress-scrubber';
import TrimRangeEditBar from './trim-range-edit-bar';
import TrimRangesBar from './trim-ranges-bar';
import CommentTimeline from '../comment-timeline';
import { ENGAGEMENT_INSIGHTS_ACCESS } from '../../../../../../shared/constants/scopes';

import TextOverlayPlayBar from 'components/share-video/text-overplay-play-bar';
import { ViewersInsightsTimeline } from 'components/share-video/viewer-insights/list';
import Slider from 'components/slider';

import './styles.less';

class SeekBar extends Component {
  state = {
    play_after_seek_end: false,
  };

  render() {
    const {
      commentsEnabled,
      controlBarExpanded,
      currentTime,
      currentTrimRange,
      duration,
      inTrimMode,
      isMainVideoOnSharePage,
      isViewerInsightsActive,
      localReactions,
      reactions,
      selectedTrimRange,
      useEmojis,
    } = this.props;

    const progressAsAPercentage = (this.getPercent() * 100).toFixed(2);

    return (
      <div
        ref={ref => (this.container = ref)}
        className={`video-player-seek-bar ${inTrimMode ? 'in-trim-mode' : ''}`}
      >
        {/*
          we want to scale the backgroud (non-loaded content) on hover but not
          the child elements
        */}
        <Slider
          ref={slider => (this.slider = slider)}
          getPercent={this.getPercent}
          label={'Video Progress'}
          onBlur={this.onMouseUp}
          onMouseDown={this.onMouseDown}
          onMouseMove={this.onMouseMove}
          onMouseUp={this.onMouseUp}
          valueNow={progressAsAPercentage}
        >
          <LoadProgressBar duration={duration} />

          <PlayProgressBar currentTime={currentTime} inTrimMode={inTrimMode} />
          {isMainVideoOnSharePage && inTrimMode && (
            <TrimRangesBar duration={duration} />
          )}

          {isMainVideoOnSharePage && inTrimMode && currentTrimRange != null && (
            <TrimRangeEditBar
              getNewTime={this.getNewTime}
              onMouseDown={this.onMouseDown}
              onMouseMove={this.onMouseMove}
              onMouseUp={this.onMouseUp}
            />
          )}

          <PlayProgressScrubber controlBarExpanded={controlBarExpanded} />

          {!inTrimMode && !isViewerInsightsActive && useEmojis && (
            <EmojiTimeline
              reactions={reactions}
              localReactions={localReactions}
              controlBarExpanded={controlBarExpanded}
              containerDimensions={this.props.containerDimensions}
              animateReactions={this.props.animateReactions}
              currentTime={currentTime}
              duration={duration}
            />
          )}

          {commentsEnabled && !inTrimMode && !isViewerInsightsActive && (
            <CommentTimeline duration={duration} />
          )}

          {isViewerInsightsActive && <ViewersInsightsTimeline />}

          <TextOverlayPlayBar />
        </Slider>
        <div className="seek-bar-background" />
        {isMainVideoOnSharePage &&
          inTrimMode &&
          currentTrimRange == null &&
          selectedTrimRange == null && (
            <AddTrimRangeTooltip getPercent={this.getPercent} />
          )}
      </div>
    );
  }

  componentDidMount() {
    window.addEventListener('resize', this.onWindowResize);

    setTimeout(() => {
      this.onWindowResize();
    }, 100);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  // throttled to fire once every 100ms
  onWindowResize = _throttle(() => {
    this.forceUpdate();
  }, 100);

  getNewTime = e => {
    const { currentTime, duration } = this.props;

    // onBlur causes component unmount and ref change to null
    if (!this.slider) {
      return currentTime;
    }

    const distance = this.slider.calculateDistance(e);
    const newTime = distance * duration;

    if (_isNaN(newTime)) {
      return currentTime;
    }

    // don't let video end while scrubbing
    return Math.min(newTime, duration - 0.01);
  };

  changeTime(e) {
    const {
      currentTime,
      currentTrimRange,
      duration,
      inTrimMode,
      trimRanges,
      ended,
    } = this.props;

    let newTime = this.getNewTime(e);

    // skip over trim ranges
    if (inTrimMode && currentTrimRange == null) {
      newTime =
        getNewTimeSkipRanges(
          Math.round(currentTime * 1000),
          Math.round(newTime * 1000),
          Math.round(duration * 1000),
          trimRanges,
          currentTrimRange,
          true
        ) / 1000;
    }

    this.props.requestVideoPlayerCurrentTime(newTime);

    if (ended) {
      this.props.requestVideoPlayerPlay();
    }
  }

  onMouseDown = () => {
    document.addEventListener('mouseup', this.onMouseUp, false);

    const { paused } = this.props;

    this.setState({ play_after_seek_end: !paused });
  };

  onMouseMove = e => {
    this.changeTime(e);
    this.props.updateVideoPlayerSeeking(true);

    const { paused } = this.props;

    if (!paused) {
      this.props.onPause();
    }
  };

  onMouseUp = e => {
    document.removeEventListener('mouseup', this.onMouseUp);

    this.changeTime(e);
    this.props.updateVideoPlayerSeeking(false);

    if (this.state.play_after_seek_end) {
      this.props.onPlay(this.state.play_after_seek_end);
    }

    this.setState({ play_after_seek_end: false });
  };

  getPercent = () => {
    const { currentTime, duration } = this.props;

    return Math.min(currentTime / duration, 1);
  };
}

const mapStateToProps = state => {
  const {
    current_trim_range: currentTrimRange,
    in_trim_mode: inTrimMode,
    selected_trim_range: selectedTrimRange,
    trim_ranges: trimRanges,
  } = state.trimming;
  const {
    reactions: reactions,
    local_reactions: localReactions,
    hide_reaction: hideReaction,
    animate_reactions: animateReactions,
    video,
    ended,
  } = state.video_player;

  const { comments_enabled: commentsEnabled, use_emojis: useEmojis } = video;

  const hasEngagementInsightsAccess = hasScope(
    state,
    ENGAGEMENT_INSIGHTS_ACCESS
  );

  return {
    currentTrimRange,
    inTrimMode,
    selectedTrimRange,
    trimRanges,
    localReactions,
    reactions,
    hideReaction,
    animateReactions,
    useEmojis,
    isViewerInsightsActive: selectIsActive(state),
    hasEngagementInsightsAccess,
    commentsEnabled,
    ended,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    requestVideoPlayerCurrentTime: currentTime =>
      dispatch(requestVideoPlayerCurrentTime(currentTime)),
    updateVideoPlayerSeeking: (seeking, playAfterSeek) =>
      dispatch(updateVideoPlayerSeeking(seeking, playAfterSeek)),
    requestVideoPlayerPlay: () => dispatch(requestVideoPlayerPlay()),

    // reactions
    updateVideoPlayerLocalReactions: reactions =>
      dispatch(updateVideoPlayerLocalReactions(reactions)),
    onHideReaction: reaction =>
      dispatch(updateVideoPlayerHideReaction(reaction)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SeekBar);
